import { createSelector } from 'reselect';
import { IInitialState } from 'store/models';
import { mapTableStatistic } from 'modules/admin-panel/pages/results/helpers/mapTableStatistic';
import { calculateSubmittedAndSatisfaction } from 'modules/admin-panel/pages/results/helpers/calculateSubmitted';
import {
	IPersonaWithImage,
	IResultMapPointCalculated,
	IResultTrendCalculated,
	IRoundCalculated,
	IPersonaData,
} from 'modules/admin-panel/pages/results/models';
import { countOfUserPics, getPersonaUserPic } from '../helpers';

export const dashboardSelector = (state: IInitialState) =>
	state.admin.results.dashboard;
export const commentsSelector = (state: IInitialState) =>
	dashboardSelector(state).comments;
export const activeCommentTypeSelector = (state: IInitialState) =>
	dashboardSelector(state).activeCommentType;
export const commentsWordcloudSelector = (state: IInitialState) =>
	dashboardSelector(state).commentsWordcloud;
export const isLoadingSelector = (state: IInitialState) =>
	dashboardSelector(state).isLoading;
export const isLoadingAllSelector = (state: IInitialState) =>
	dashboardSelector(state).isLoadingAll;
export const isContentLoadingSelector = (state: IInitialState) =>
	dashboardSelector(state).isContentLoading;
export const isGridStatisticLoadingSelector = (state: IInitialState) =>
	dashboardSelector(state).isGridStatisticLoading;
export const errorSelector = (state: IInitialState) =>
	dashboardSelector(state).error;
export const roundsSelector = (state: IInitialState) =>
	dashboardSelector(state).rounds;
export const calculatedRoundsSelector = createSelector(
	roundsSelector,
	(rounds): IRoundCalculated[] | undefined =>
		rounds?.map(calculateSubmittedAndSatisfaction),
);
export const roundsErrorMessageSelector = (state: IInitialState) =>
	dashboardSelector(state).roundsErrorMessage;
export const activeRoundIdSelector = (state: IInitialState) =>
	dashboardSelector(state).activeRound;
export const activeRoundSelector = createSelector(
	calculatedRoundsSelector,
	activeRoundIdSelector,
	(rounds, activeRoundId) =>
		rounds?.find(({ surveyRoundId }) => surveyRoundId === activeRoundId),
);
export const servicesSelector = (state: IInitialState) =>
	dashboardSelector(state).services;
export const activeServiceIdSelector = (state: IInitialState) =>
	dashboardSelector(state).activeService;
export const activeServiceSelector = createSelector(
	servicesSelector,
	activeServiceIdSelector,
	(services, activeServiceId) =>
		services?.find(({ id }) => id === activeServiceId),
);
export const rolesSelector = (state: IInitialState) =>
	dashboardSelector(state).roles;
export const activeRoleIdSelector = (state: IInitialState) =>
	dashboardSelector(state).activeRole;
export const activeRoleSelector = createSelector(
	rolesSelector,
	activeRoleIdSelector,
	(roles, activeRoleId) => roles?.find(({ id }) => id === activeRoleId),
);
export const businessUnitsSelector = (state: IInitialState) =>
	dashboardSelector(state).businessUnits;
export const regionsSelector = (state: IInitialState) =>
	dashboardSelector(state).regions;
export const resetFiltersSelector = (state: IInitialState) =>
	dashboardSelector(state).filtersToReset;

export const trendsSelector = (state: IInitialState) =>
	dashboardSelector(state).trends;
export const calculatedTrendsSelector = createSelector(
	trendsSelector,
	(trends): IResultTrendCalculated[] | undefined =>
		trends?.map(calculateSubmittedAndSatisfaction),
);
export const compareTrendsSelector = (state: IInitialState) =>
	dashboardSelector(state).trends;
export const calculatedCompareTrendsSelector = createSelector(
	compareTrendsSelector,
	(trends): IResultTrendCalculated[] | undefined =>
		trends?.map(calculateSubmittedAndSatisfaction),
);
export const activeTrendSelector = createSelector(
	calculatedTrendsSelector,
	activeRoundIdSelector,
	(trends, roundId) => trends?.find((trend) => trend.roundId === roundId),
);
export const mapDataSelector = (state: IInitialState) =>
	dashboardSelector(state).map;
export const mapPrevDataSelector = (state: IInitialState) =>
	dashboardSelector(state).mapPrev;
export const mapAllDataSelector = createSelector(
	mapDataSelector,
	mapPrevDataSelector,
	(
		map,
		mapPrev,
	): {
		map?: IResultMapPointCalculated[];
		mapPrev?: IResultMapPointCalculated[];
	} => ({
		map: map?.map(calculateSubmittedAndSatisfaction),
		mapPrev: mapPrev?.map(calculateSubmittedAndSatisfaction),
	}),
);
export const countrySelector = (state: IInitialState) =>
	dashboardSelector(state).country;
export const citySelector = (state: IInitialState) =>
	dashboardSelector(state).city;
export const officeSelector = (state: IInitialState) =>
	dashboardSelector(state).office;
export const trendsCardsSelector = (state: IInitialState) =>
	dashboardSelector(state).servicesCards;
export const isReportLoadingSelector = (state: IInitialState) =>
	dashboardSelector(state).isReportLoading;
export const surveyNameSelector = (state: IInitialState) =>
	dashboardSelector(state).surveyName || '';
export const personaListSelector = (state: IInitialState) =>
	dashboardSelector(state).personaList;
export const personaListWithImageSelector = createSelector(
	personaListSelector,
	(personaList?: IPersonaData[]) => {
		if (!personaList) return;

		const usedImg: string[] = [];
		return personaList.map((persona, index) => {
			if (index >= countOfUserPics) {
				usedImg.length = 0;
			}
			const image = getPersonaUserPic(usedImg);
			usedImg.push(image);

			return {
				...persona,
				image,
			} as IPersonaWithImage;
		});
	},
);
export const scalePointsSelector = (state: IInitialState) =>
	dashboardSelector(state).scalePoints;
export const scaleRangeSelector = (state: IInitialState) =>
	dashboardSelector(state).scaleRange;

export const isSurveyQuickSelector = (state: IInitialState) =>
	dashboardSelector(state).isSurveyQuick;
export const npsTrendsSelector = (state: IInitialState) =>
	dashboardSelector(state).npsTrends;
export const quickQuestionAnswersSelector = (state: IInitialState) =>
	dashboardSelector(state).quickQuestionAnswers;
export const quickQuestionSelector = (state: IInitialState) =>
	quickQuestionAnswersSelector(state)?.quickQuestion;
export const activeAnswersTypeSelector = (state: IInitialState) =>
	dashboardSelector(state).activeAnswersType;
export const quickAnswersWordcloudSelector = (state: IInitialState) =>
	dashboardSelector(state).quickQuestionAnswersWorcloud;

export const performanceSlidersNamesSelector = (state: IInitialState) =>
	dashboardSelector(state).performanceSlidersNames;

export const activeServiceExamplesSelector = (state: IInitialState) =>
	dashboardSelector(state).activeServiceExamples;

export const statisticSelector = (state: IInitialState) =>
	dashboardSelector(state).statistic;
export const tableDataSelector = createSelector(
	statisticSelector,
	(statistic) => mapTableStatistic(statistic),
);
export const statisticGroupingTypeSelector = (state: IInitialState) =>
	dashboardSelector(state).statisticGroupingType;

export const serviceStatsSelector = (state: IInitialState) =>
	dashboardSelector(state).serviceStats;
